import { useNavigate } from "@tanstack/react-router";
import { ChevronLeftDouble, ShieldTick, User01 } from "@untitledui/icons";
import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import type { NavItemType } from "@/components/application/app-navigation/config";
import { Button } from "@/components/base/buttons/button";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { Organization } from "@/services/organization";
import { cx } from "@/utils/cx";
import { ViewTransitionTypes, viewTransition } from "@/utils/view-transitions";
import type { AccountMode } from "../NavCard/account-mode";
import { AccountMode as AccountModeConst } from "../NavCard/account-mode";
import { NavCard } from "../NavCard/NavCard";
import { NavHeader } from "../NavHeader/NavHeader";
import { CollapsedNavList } from "../NavList/CollapsedNavList";
import { NavList } from "../NavList/NavList";
import { SidebarToggle } from "../SidebarToggle/SidebarToggle";
import { MobileLeftNav } from "./MobileLeftNav";

const LOGO_PORTAL = "/images/AdminLogo.png";
const LOGO_APP = "/images/LogoFull@2x.png";
/** Collapsed brand mark — LogoFull icon crop. */
const LOGO_MINIMAL = "/images/LogoMinimal.png";

interface SidebarNavigationProps {
	/** List of items to display. */
	items: NavItemType[];
	/** Feature card to display. */
	featureCard?: ReactNode;
	/** Whether to show the first nav card. */
	showOrgNavCard?: boolean;
	/** Whether to show the second nav card. */
	showAppNavCard?: boolean;
	/** Whether to hide the right side border. */
	hideBorder?: boolean;
	/** Additional CSS classes to apply to the sidebar. */
	className?: string;
	/** Custom background color class for active items */
	activeBackgroundClass?: string;
	/** Custom class for the main level icons */
	iconClassName?: string;
	/** First avatar image source URL or icon component */
	avatar1Src?: string | FC<{ className?: string }>;
	/** First avatar alt text */
	avatar1Alt?: string;
	/** First avatar initials fallback */
	avatar1Initials?: string;
	/** Second avatar image source URL or icon component */
	avatar2Src?: string | FC<{ className?: string }>;
	/** Second avatar alt text */
	avatar2Alt?: string;
	/** Second avatar initials fallback */
	avatar2Initials?: string;
	/** First avatar background color class */
	avatar1BackgroundColor?: string;
	/** Second avatar background color class */
	avatar2BackgroundColor?: string;
	/** First user name */
	user1Name?: string;
	/** Tooltip shown on hover for the organization selector */
	orgNavTooltipTitle?: string;
	/** Second user name */
	user2Name?: string;
	/** Home URL for logo link */
	homeUrl?: string;
	/** Logo image src for brand variant header. Defaults from accountMode. */
	logoSrc?: string;
	/** Icon-only mark for the collapsed brand state. Defaults to LogoMinimal.png. */
	logoIconSrc?: string;
	/** Header text to display (light variant only) */
	headerText?: string;
	/** Whether to show the first nav card selector dropdown */
	showOrgNavSelector?: boolean;
	/** Whether to show the second nav card selector dropdown */
	showAppNavSelector?: boolean;
	/** User name */
	name: string;
	/** User email */
	email: string;
	/** On sign out function */
	onSignOut: () => void;
	/** Opens user profile (e.g. profile drawer) */
	onUserProfile?: () => void;
	/** Available organizations */
	availableOrganizations: Organization[];
	/** Current organization */
	currentOrganization: Organization;
	/** Whether to disable the organization selector dropdown */
	disabled?: boolean;
	/** Whether the user is a platform admin */
	isPlatformAdmin?: boolean;
	/** Whether the user is an org admin */
	isOrgAdmin?: boolean;
	/** On main app nav card click handler */
	mainAppNavCardOnClickHandler?: () => void;
	/** On admin app nav card click handler */
	adminAppNavCardOnClickHandler?: () => void;
	/**
	 * Visual variant.
	 * "brand" — dark gradient sidebar matching Figma Admin Portal design.
	 * "light" — default light gray sidebar.
	 */
	variant?: "light" | "brand";
	/**
	 * Footer account card mode (Figma views).
	 * portal | application | standard
	 */
	accountMode?: AccountMode;
}

// On-brand class constants (Figma NavItem / accountCard)
const BRAND_SELECTED_BG = "bg-[rgba(255,255,255,0.1)]";
const BRAND_ICON_CLASS = "text-[rgba(255,255,255,0.6)]";
const BRAND_HOVER_CLASS = "hover:bg-[rgba(255,255,255,0.1)]";
/** Figma NavAccountCard avatar — gray-modern/700 + alpha-white-10 border only */
const ACCOUNT_AVATAR_BG =
	"bg-[#364152] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)]";
const ACCOUNT_AVATAR_ICON_CLASS = "text-[rgba(255,255,255,0.9)] stroke-[2.5px]";

export const LeftNav = ({
	items,
	showOrgNavCard = true,
	showAppNavCard = true,
	hideBorder = false,
	className,
	activeBackgroundClass = "bg-active",
	iconClassName = "text-gray-600",
	avatar1Src,
	avatar1Alt,
	avatar1Initials = "",
	avatar2Src,
	avatar2Alt,
	avatar2Initials = "",
	avatar1BackgroundColor,
	avatar2BackgroundColor,
	user1Name,
	orgNavTooltipTitle,
	user2Name,
	homeUrl = "/web/$orgId/home",
	logoSrc,
	logoIconSrc = LOGO_MINIMAL,
	headerText,
	showOrgNavSelector = true,
	showAppNavSelector = true,
	name,
	email,
	onSignOut,
	onUserProfile,
	availableOrganizations,
	currentOrganization,
	disabled = false,
	isPlatformAdmin = false,
	isOrgAdmin = false,
	mainAppNavCardOnClickHandler = () => {},
	adminAppNavCardOnClickHandler = () => {},
	variant = "light",
	accountMode,
}: SidebarNavigationProps) => {
	const { t } = useAppTranslation();
	const navigate = useNavigate();

	const isBrand = variant === "brand";

	/** Brand collapsed always uses LogoMinimal; light may pass a custom mark. */
	const resolvedLogoIconSrc = isBrand ? LOGO_MINIMAL : logoIconSrc;

	// Derive resolved props
	const resolvedAvatar1Alt = avatar1Alt ?? t("main.nav.user.avatar");
	const resolvedAvatar2Alt = avatar2Alt ?? t("main.nav.user.avatar2");
	const resolvedUser1Name = user1Name ?? t("main.nav.user.name");
	const resolvedUser2Name = user2Name ?? t("main.nav.user.name2");
	const resolvedHeaderText = headerText ?? t("main.nav.header.workflow");
	const collapsedOrgNavTooltipTitle = orgNavTooltipTitle ?? resolvedUser1Name;

	const resolvedLogoSrc =
		logoSrc ??
		(accountMode === AccountModeConst.Portal ? LOGO_PORTAL : LOGO_APP);

	const resolvedIconClass = isBrand ? BRAND_ICON_CLASS : iconClassName;

	const accountFooter =
		accountMode === AccountModeConst.Portal
			? {
					title: t("admin.menu.title"),
					avatarSrc: ShieldTick as FC<{ className?: string }>,
					avatarInitials: "",
					backgroundColor: ACCOUNT_AVATAR_BG,
					iconClassName: ACCOUNT_AVATAR_ICON_CLASS,
				}
			: accountMode === AccountModeConst.Application
				? {
						title: t("main.nav.footer.application"),
						avatarSrc: ShieldTick as FC<{ className?: string }>,
						avatarInitials: "",
						backgroundColor: ACCOUNT_AVATAR_BG,
						iconClassName: ACCOUNT_AVATAR_ICON_CLASS,
					}
				: accountMode === AccountModeConst.Standard
					? {
							title: name,
							avatarSrc: User01 as FC<{ className?: string }>,
							avatarInitials: "",
							backgroundColor: ACCOUNT_AVATAR_BG,
							iconClassName: ACCOUNT_AVATAR_ICON_CLASS,
						}
					: {
							title: resolvedUser2Name,
							avatarSrc: avatar2Src,
							avatarInitials: avatar2Initials,
							backgroundColor: avatar2BackgroundColor,
							iconClassName: resolvedIconClass,
						};

	// Brand-variant derived values
	const resolvedActiveClass = isBrand
		? BRAND_SELECTED_BG
		: activeBackgroundClass;
	const resolvedHoverClass = isBrand ? BRAND_HOVER_CLASS : undefined;

	/** Footer account card — always for brand account modes; else gated by showAppNavSelector */
	const showAccountFooter =
		showAppNavCard && (accountMode != null || showAppNavSelector);

	// Initialize state from localStorage, default to true if not found
	const [isExpanded, setIsExpanded] = useState<boolean>(() => {
		if (typeof window !== "undefined") {
			const saved = localStorage.getItem("leftNav-expanded");
			return saved !== null ? JSON.parse(saved) : true;
		}
		return true;
	});

	/** Exclusive: only one of org | account menu open in the sidebar */
	const [openSidebarMenu, setOpenSidebarMenu] = useState<
		"org" | "account" | null
	>(null);

	function handleOrgMenuOpenChange(isOpen: boolean) {
		setOpenSidebarMenu(isOpen ? "org" : null);
	}

	function handleAccountMenuOpenChange(isOpen: boolean) {
		setOpenSidebarMenu(isOpen ? "account" : null);
	}

	/** Shared org switcher props — expanded + collapsed layers stay in sync */
	const orgNavCardProps = {
		disabled,
		avatarSrc: avatar1Src,
		avatarAlt: resolvedAvatar1Alt,
		avatarInitials: avatar1Initials,
		avatarBackgroundColor: avatar1BackgroundColor,
		iconClassName: resolvedIconClass,
		title: resolvedUser1Name,
		showSelector: showOrgNavSelector,
		menuType: "organization" as const,
		availableOrganizations,
		currentOrganization,
		isPlatformAdmin,
		isOrgAdmin,
		variant,
		onOpenChange: handleOrgMenuOpenChange,
	};

	/** Shared footer account props — expanded + collapsed layers stay in sync */
	const accountNavCardProps = {
		avatarSrc: accountFooter.avatarSrc,
		iconClassName: accountFooter.iconClassName,
		avatarAlt: resolvedAvatar2Alt,
		avatarInitials: accountFooter.avatarInitials,
		avatarBackgroundColor: accountFooter.backgroundColor,
		title: accountFooter.title,
		menuType: "application" as const,
		availableOrganizations,
		currentOrganization,
		isPlatformAdmin,
		isOrgAdmin,
		variant,
		accountMode,
		onUserProfile,
		onSignOut,
		mainAppNavCardOnClickHandler,
		adminAppNavCardOnClickHandler,
		onOpenChange: handleAccountMenuOpenChange,
	};

	// Persist expand preference + CSS vars (single write path)
	/** Figma Navigation Open: 300px / Closed: 88px */
	const MAIN_SIDEBAR_WIDTH = isBrand ? 300 : 319;
	const SLIM_SIDEBAR_WIDTH = isBrand ? 88 : 74;

	useEffect(() => {
		if (typeof window !== "undefined") {
			localStorage.setItem("leftNav-expanded", JSON.stringify(isExpanded));
			const navWidth = isExpanded
				? `${MAIN_SIDEBAR_WIDTH}px`
				: `${SLIM_SIDEBAR_WIDTH}px`;
			document.documentElement.style.setProperty("--left-nav-width", navWidth);
			document.documentElement.style.setProperty(
				"--tanstack-devtools-left",
				isExpanded ? "8px" : `${SLIM_SIDEBAR_WIDTH + 8}px`,
			);
		}
	}, [isExpanded, MAIN_SIDEBAR_WIDTH, SLIM_SIDEBAR_WIDTH]);

	/** Portal AdminLogo: 163×40; Application LogoFull: 159.6×29 */
	const brandLogoClassName =
		accountMode === AccountModeConst.Portal
			? "h-10 w-[163px] object-contain object-left shrink-0"
			: "h-[29px] w-[160px] object-contain shrink-0";

	// ── Brand logo header (mobile drawer — compact, no full NavHeader padding) ──
	const brandLogoHeader = isBrand && (
		<div className="shrink-0 flex items-center px-8 pt-6 pb-4">
			<button
				type="button"
				aria-label={t("main.nav.goToHomepage")}
				onClick={() =>
					navigate({
						to: homeUrl,
						...viewTransition(ViewTransitionTypes.FADE),
					})
				}
				className="rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 cursor-pointer bg-transparent border-0 p-0"
			>
				<img
					src={resolvedLogoSrc}
					alt={t("common.logoAlt")}
					className={brandLogoClassName}
				/>
			</button>
		</div>
	);

	// ── Mobile content ───────────────────────────────────────────────────────
	const mobileContent = (
		<aside
			style={{
				width: MAIN_SIDEBAR_WIDTH,
				...(isBrand && {
					background: "var(--gradient-brand-primary02)",
				}),
			}}
			className={cx(
				"flex h-full w-full max-w-full flex-col pt-4 lg:pt-5 transition-all ease-in-out duration-300",
				!isBrand && "bg-gray-50",
				!hideBorder && !isBrand && "border-secondary md:border-r",
				className,
			)}
		>
			{brandLogoHeader}
			{showOrgNavCard && (
				<div className="shrink-0 px-2 py-2 lg:px-4">
					<NavCard
						disabled={disabled}
						avatarSrc={avatar1Src}
						title={resolvedUser1Name}
						avatarAlt={resolvedAvatar1Alt}
						avatarInitials={avatar1Initials}
						avatarBackgroundColor={avatar1BackgroundColor}
						showSelector={showOrgNavSelector}
						menuType="organization"
						availableOrganizations={availableOrganizations}
						currentOrganization={currentOrganization}
						isPlatformAdmin={isPlatformAdmin}
						isOrgAdmin={isOrgAdmin}
						variant={variant}
						isOpen={openSidebarMenu === "org"}
						onOpenChange={handleOrgMenuOpenChange}
					/>
				</div>
			)}
			<div className="min-h-0 flex-1 overflow-y-auto transition-opacity duration-300">
				<NavList
					items={items}
					selectedClassName={resolvedActiveClass}
					iconClassName={resolvedIconClass}
					hoverClassName={resolvedHoverClass}
					variant={variant}
				/>
			</div>

			{/* Footer section for mobile - always expanded */}
			{showAccountFooter && (
				<div className="shrink-0 flex flex-col gap-2 px-2 py-2 lg:px-4">
					<NavCard
						avatarSrc={accountFooter.avatarSrc}
						iconClassName={accountFooter.iconClassName}
						title={accountFooter.title}
						subtitle={isBrand || accountMode != null ? email : undefined}
						avatarAlt={resolvedAvatar2Alt}
						avatarInitials={accountFooter.avatarInitials}
						avatarBackgroundColor={accountFooter.backgroundColor}
						menuType="application"
						availableOrganizations={availableOrganizations}
						currentOrganization={currentOrganization}
						isPlatformAdmin={isPlatformAdmin}
						isOrgAdmin={isOrgAdmin}
						variant={variant}
						accountMode={accountMode}
						onUserProfile={onUserProfile}
						onSignOut={onSignOut}
						mainAppNavCardOnClickHandler={mainAppNavCardOnClickHandler}
						adminAppNavCardOnClickHandler={adminAppNavCardOnClickHandler}
						isOpen={openSidebarMenu === "account"}
						onOpenChange={handleAccountMenuOpenChange}
					/>
				</div>
			)}
		</aside>
	);

	// ── Desktop content ──────────────────────────────────────────────────────
	const desktopContent = (
		<aside
			style={{
				width: isExpanded ? MAIN_SIDEBAR_WIDTH : SLIM_SIDEBAR_WIDTH,
				...(isBrand && {
					background: "var(--gradient-brand-primary02)",
				}),
			}}
			className={cx(
				"flex h-full w-full max-w-full flex-col transition-all ease-in-out",
				isExpanded ? "duration-300" : "duration-500",
				!isBrand && "bg-gray-50",
				!hideBorder && !isBrand && "border-secondary md:border-r",
				className,
			)}
		>
			{/* ── Header: NavHeader (brand) + org switcher (crossfade like NavList) ── */}
			{isBrand ? (
				<>
					<NavHeader
						isCollapsed={!isExpanded}
						logoSrc={resolvedLogoSrc}
						logoIconSrc={resolvedLogoIconSrc}
						logoAlt={t("common.logoAlt")}
						logoClassName={brandLogoClassName}
						homeUrl={homeUrl}
						homeAriaLabel={t("main.nav.goToHomepage")}
					/>
					{showOrgNavCard && (
						/*
						 * Both layers stay absolute so layout width follows the
						 * animating aside — no in-flow swap that flashes full-width.
						 */
						<div className="relative shrink-0 h-11 mx-2 mb-3 lg:mx-4 overflow-hidden">
							<div
								className={cx(
									"absolute inset-0 flex items-center transition-opacity duration-300",
									isExpanded ? "opacity-100" : "opacity-0 pointer-events-none",
								)}
							>
								<NavCard
									{...orgNavCardProps}
									isOpen={isExpanded && openSidebarMenu === "org"}
								/>
							</div>
							<div
								className={cx(
									"absolute inset-0 flex items-center justify-center transition-opacity duration-300",
									isExpanded ? "opacity-0 pointer-events-none" : "opacity-100",
								)}
							>
								<Tooltip
									title={collapsedOrgNavTooltipTitle}
									placement="right"
									isDisabled={!collapsedOrgNavTooltipTitle}
								>
									<NavCard
										{...orgNavCardProps}
										isCollapsed
										isOpen={!isExpanded && openSidebarMenu === "org"}
									/>
								</Tooltip>
							</div>
						</div>
					)}
				</>
			) : (
				<div
					className={cx(
						"relative flex flex-col border-secondary border-b",
						isExpanded ? "p-4" : "px-2 py-2 lg:px-4",
					)}
				>
					{showOrgNavCard && (
						<div className="relative h-11 w-full overflow-hidden">
							<div
								className={cx(
									"absolute inset-0 flex items-center transition-opacity duration-300",
									isExpanded ? "opacity-100" : "opacity-0 pointer-events-none",
								)}
							>
								<NavCard
									{...orgNavCardProps}
									isOpen={isExpanded && openSidebarMenu === "org"}
								/>
							</div>
							<div
								className={cx(
									"absolute inset-0 flex items-center justify-center transition-opacity duration-300",
									isExpanded ? "opacity-0 pointer-events-none" : "opacity-100",
								)}
							>
								<Tooltip
									title={collapsedOrgNavTooltipTitle}
									placement="right"
									isDisabled={!collapsedOrgNavTooltipTitle}
								>
									<NavCard
										{...orgNavCardProps}
										isCollapsed
										isOpen={!isExpanded && openSidebarMenu === "org"}
									/>
								</Tooltip>
							</div>
						</div>
					)}
				</div>
			)}

			{/* ── Nav list ── */}
			<div className="relative mt-4 min-h-0 flex-1 overflow-y-auto">
				{/* Expanded */}
				<div
					className={cx(
						"transition-opacity duration-300",
						isExpanded ? "opacity-100" : "opacity-0 delay-200",
						!isExpanded && "absolute inset-0 pointer-events-none",
					)}
				>
					<NavList
						items={items}
						selectedClassName={resolvedActiveClass}
						iconClassName={resolvedIconClass}
						hoverClassName={resolvedHoverClass}
						variant={variant}
					/>
				</div>

				{/* Collapsed icon-only — center in the 88px rail */}
				<div
					className={cx(
						"flex flex-col items-center px-2 transition-opacity duration-300",
						isExpanded ? "opacity-0" : "opacity-100 delay-200",
						isExpanded && "absolute inset-0 pointer-events-none",
					)}
				>
					<CollapsedNavList
						items={items}
						selectedClassName={resolvedActiveClass}
						iconClassName={resolvedIconClass}
						hoverClassName={resolvedHoverClass}
						variant={variant}
					/>
				</div>
			</div>

			{/* ── Footer ── */}
			<div className="shrink-0 flex flex-col gap-2">
				{/* Light-variant header text */}
				{!isBrand && (
					<div className="relative min-h-9 px-4">
						<div
							className={cx(
								"flex items-center gap-2 text-[#A4A7AE] text-sm font-semibold leading-5 font-body transition-opacity duration-300",
								isExpanded ? "opacity-100" : "opacity-0 pointer-events-none",
							)}
						>
							{resolvedHeaderText}
							{isPlatformAdmin && headerText === t("org-admin./.title") && (
								<Tooltip
									title={t("org-admin./.backToPlatformAdmin")}
									placement="top"
								>
									<Button
										color="tertiary"
										className="text-gray-500 p-1 hover:text-gray-700"
										onClick={() => {
											navigate({ to: "/admin/home" });
										}}
										iconLeading={ChevronLeftDouble}
									/>
								</Tooltip>
							)}
						</div>
					</div>
				)}

				{showAccountFooter && (
					/*
					 * Fixed 54px band + both layers absolute: card width tracks the
					 * aside transition instead of swapping in-flow full ↔ circle.
					 */
					<div
						className={cx(
							"relative h-[54px] overflow-hidden",
							isBrand
								? "mx-(--spacing-xl,16px) my-(--spacing-xl,16px)"
								: "mx-0 my-4",
						)}
					>
						<div
							className={cx(
								"absolute inset-0 flex items-center transition-opacity duration-300",
								isExpanded ? "opacity-100" : "opacity-0 pointer-events-none",
							)}
						>
							<NavCard
								{...accountNavCardProps}
								subtitle={isBrand || accountMode != null ? email : undefined}
								isOpen={isExpanded && openSidebarMenu === "account"}
							/>
						</div>
						<div
							className={cx(
								"absolute inset-0 flex items-center justify-center transition-opacity duration-300",
								isExpanded ? "opacity-0 pointer-events-none" : "opacity-100",
							)}
						>
							<NavCard
								{...accountNavCardProps}
								isCollapsed
								isOpen={!isExpanded && openSidebarMenu === "account"}
							/>
						</div>
					</div>
				)}
			</div>

			{/* ── Sidebar toggle (Figma 3970:31741) — no hr; bar bg separates ── */}
			<SidebarToggle
				isCollapsed={!isExpanded}
				variant={variant}
				label={
					isExpanded ? t("main.nav.menu.collapse") : t("main.nav.menu.expand")
				}
				onToggle={() => {
					setOpenSidebarMenu(null);
					setIsExpanded((prev) => !prev);
				}}
			/>
		</aside>
	);

	return (
		<>
			<MobileLeftNav
				homeUrl={homeUrl}
				name={name}
				email={email}
				onSignOut={onSignOut}
				onUserProfile={onUserProfile}
				variant={variant}
				logoSrc={resolvedLogoSrc}
				logoClassName={brandLogoClassName}
			>
				{mobileContent}
			</MobileLeftNav>

			{/* Desktop navigation */}
			<div className="hidden lg:block">{desktopContent}</div>
		</>
	);
};
